import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { modifier } from 'ember-modifier';
import CheckboxControl from '../../../components/form/controls/checkbox.gts';

export interface Signature {
  Element: HTMLFieldSetElement;
  Args: { legend?: string; activable?: boolean };
  Blocks: {
    col1: [];
    col2?: [];
    legend?: [];
  };
}

const STYLES = {
  base: [
    'flex',
    'flex-col',
    'sm:grid',
    'sm:gap-6',
    'sm:grid-cols-3',
    'mb-6',
    'sm:mb-0',
    '[&:first-child>*]:sm:rounded-tr',
    '[&:last-child>*]:sm:rounded-br',
    '[&:last-child]:mb-2',

    /****** Cells ******/
    '[&>*]:px-0',
    '[&>*]:sm:py-66',

    // first cell (legend)
    '[&>legend:first-child]:sm:bg-gradient-to-r',
    '[&>legend:first-child]:sm:from-white',
    '[&>legend:first-child]:sm:to-grey-light',
    '[&>legend:first-child]:text-purple',
    '[&>legend:first-child]:sm:text-right',
    '[&>legend:first-child]:sm:px-4',
    '[&>legend:first-child]:float-left',
    '[&>legend:first-child]:flex',
    '[&>legend:first-child]:items-center',
    '[&>legend:first-child]:justify-end',
    '[&>legend:first-child]:gap-2',

    // Legend checkbox
    '[&>legend:first-child>input[type="checkbox"]]:border-purple',
    '[&>legend:first-child>input[type="checkbox"]]:ring-purple'
  ]
} as const;

export default class WrFormRowsFieldsetComponent extends Component<Signature> {
  @tracked needsColspan = false;
  @tracked isActive: boolean;

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args);
    if (args.activable == true) {
      this.isActive = true;
    } else if (args.activable == undefined) {
      this.isActive = true;
    } else {
      this.isActive = false;
    }
  }

  get styles() {
    const col2Styles = this.needsColspan
      ? ['[&>*:nth-child(2)]:col-span-2', '[&>*:nth-child(3)]:hidden']
      : [];
    return [...STYLES.base, ...col2Styles].join(' ');
  }

  toggleActive = () => {
    this.isActive = !this.isActive;
  };

  get isDisabled() {
    return !this.isActive;
  }

  checkEmpty = modifier(element => {
    if (element.childElementCount == 0) this.needsColspan = true;
  });

  <template>
    <fieldset class={{this.styles}} disabled={{this.isDisabled}} ...attributes>
      <legend class='py-6'>
        {{#if (has-block 'legend')}}
          {{yield to='legend'}}
        {{else}}
          {{@legend}}
        {{/if}}

        {{#if @activable}}
          <CheckboxControl
            {{on 'click' this.toggleActive}}
            checked={{this.isActive}}
          />
        {{/if}}
      </legend>

      {{! second cell }}
      <div>{{yield to='col1'}}</div>

      <div {{this.checkEmpty}}>{{yield to='col2'}}</div>
    </fieldset>
  </template>
}
